import {
  AggressorFlagEnum,
  Box,
  Button,
  ButtonGroup,
  ButtonVariants,
  EMPTY_ARRAY,
  FeeModeEnum,
  Flex,
  FormControlSizes,
  IconName,
  LiquidityTypeEnum,
  MixpanelEvent,
  SideEnum,
  SpreadUnit,
  Text,
  Tooltip,
  getPriceDiff,
  getSpreadUnit,
  toBigWithDefault,
  useMarketAccountsContext,
  useMarketDataSnapshot,
  useMarketsAndMarketAccountsForMarketDataSnapshot,
  useMixpanel,
  useObservableValue,
  type MarketDataSnapshot,
  type OrderIntent,
} from '@talos/kyoko';
import { flatten, noop } from 'lodash-es';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { map } from 'rxjs/operators';
import { ORDER_BOOK_ITEM_HEIGHT } from '../../containers/Trading/DeepDive/dimensions';
import { useIsPricingModeBPS, useOwnOrders } from '../../hooks';
import { useCombinedMarketsAndAccounts } from '../../hooks/useCombinedMarketsAndAccounts';
import { useMarketDataTrade } from '../../hooks/useMarketDataTrade';
import { useDealerMarkets, useExchangeMarkets } from '../../hooks/useMarketsOfType';
import { PriceIncrement } from '../Ladder/PriceIncrement';
import { ClickToTradeOrderPreview } from '../MarketDataCard/ClickToTradeOrderPreview';
import { OrderBookLevel } from './OrderBookLevel';
import { TradeDirection } from './TradeDirection';
import { Header, MiddleRowWrapper, Row } from './styles';
import { LastPriceType, type OrderBookParams, type OrderBookSnapshotLevel } from './types';
import { getLevelPrice, getOrderBookLevels, getOwnOrdersByPrice, spreadBps } from './utils';

const TRADE_DIRECTION_BORDER_HEIGHT = 1;
const TRADE_DIRECTION_HEIGHT_ADJUSTMENT = ORDER_BOOK_ITEM_HEIGHT / 2 + TRADE_DIRECTION_BORDER_HEIGHT * 2;

export const OrderBook = memo(
  ({
    security,
    priceIncrement,
    hideLastTraded = false,
    selectedMarkets,
    selectedMarketAccounts,
    showCumQty,
    onCumQtyClick: handleCumQtyClick,
    showAllInPrices,
    showFirmLiquidity,
    onRowClick,
    onIncrementChange,
    showPriceIncrementSetting,
    onMouseLeaveRow,
    clickToTradePreviewOrder,
  }: OrderBookParams) => {
    const mixpanel = useMixpanel();
    const { marketAccountsByMarket } = useMarketAccountsContext();
    const [lastPriceType, setLastPriceType] = useState<LastPriceType>(
      hideLastTraded ? LastPriceType.MarketMid : LastPriceType.LastTraded
    );
    const [depth, setDepth] = useState(10);
    const refs = useRef<Array<HTMLDivElement | null>>([]);
    const [total, setTotal] = useState(0);

    const isPricingModeBPS = useIsPricingModeBPS(security);
    const differenceUnit = getSpreadUnit(security, isPricingModeBPS);

    const selectedCombined = useCombinedMarketsAndAccounts(selectedMarketAccounts, selectedMarkets);

    const selectedDealerMarkets = useDealerMarkets(selectedCombined, false);
    const onlyDealersSelected = selectedCombined.length > 0 && selectedDealerMarkets.length > 0;

    const marketAccounts = useMemo(() => {
      const markets = security?.Markets ?? [];
      return selectedMarketAccounts.length > 0
        ? selectedMarketAccounts
        : flatten(
            markets.map(securityMarket => {
              return marketAccountsByMarket.get(securityMarket)?.map(ma => ma.Name);
            }) ?? []
          ).filter(name => name);
    }, [selectedMarketAccounts, marketAccountsByMarket, security?.Markets]);

    const { ownBids, ownOffers } = useOwnOrders({
      symbol: security.Symbol,
      markets: selectedMarkets,
      marketAccounts: marketAccounts as string[],
    });

    const myBids = useObservableValue(() => ownBids.pipe(map(bids => getOwnOrdersByPrice(bids))), [ownBids]);
    const myOffers = useObservableValue(() => ownOffers.pipe(map(offers => getOwnOrdersByPrice(offers))), [ownOffers]);

    const snapshotMarkets = useMarketsAndMarketAccountsForMarketDataSnapshot(
      selectedCombined.length > 0 ? selectedCombined : (security.Markets ?? [])
    );

    const { marketDataSnapshots, tradeDirections } = useMarketDataSnapshot({
      symbol: security.Symbol,
      currency: security.BaseCurrency,
      markets: snapshotMarkets,
      depth,
      tag: 'OrderBook',
      sizeBuckets: security.SizeBuckets as string[],
      priceIncrement,
      includeTradeDirection: true,
      feeMode: showAllInPrices ? FeeModeEnum.Taker : undefined,
      liquidityType: showFirmLiquidity ? LiquidityTypeEnum.Firm : LiquidityTypeEnum.Indicative,
      throttle: '200ms',
    });
    const [offers, setOffers] = useState<OrderBookSnapshotLevel[]>([]);
    const [bids, setBids] = useState<OrderBookSnapshotLevel[]>([]);

    const selectedExchangeMarkets = useExchangeMarkets(selectedCombined, false);

    const { marketDataTradeObs } = useMarketDataTrade({
      symbol: security.Symbol,
      markets: selectedExchangeMarkets.length > 0 ? selectedExchangeMarkets : (security.Markets ?? EMPTY_ARRAY),
      limit: 1,
      withHistoricalTrades: !hideLastTraded,
      tag: 'OrderBook',
    });

    useEffect(() => {
      const subscription = marketDataSnapshots.subscribe((snapshot: MarketDataSnapshot) => {
        const { offers, bids, offersSizeSum, bidsSizeSum } = getOrderBookLevels(snapshot, depth);

        setTotal(Math.max(offersSizeSum, bidsSizeSum));
        setOffers(offers);
        setBids(bids);
      });

      return () => {
        subscription.unsubscribe();
      };
    }, [marketDataSnapshots, depth]);

    useEffect(() => {
      setLastPriceType(onlyDealersSelected || hideLastTraded ? LastPriceType.MarketMid : LastPriceType.LastTraded);
    }, [onlyDealersSelected, setLastPriceType, hideLastTraded]);

    useLayoutEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          const maxHeight = refs.current.reduce((acc: number, el) => {
            if (!el) {
              return acc;
            }
            const { height } = el.getBoundingClientRect();
            acc = Math.max(acc, height);
            return acc;
          }, 0);
          const depth = Math.ceil(maxHeight / ORDER_BOOK_ITEM_HEIGHT);
          setDepth(Math.max(10, depth));
        });
      });

      refs.current.forEach(ref => {
        if (!ref) {
          return;
        }
        resizeObserver.observe(ref);
      });

      return () => resizeObserver.disconnect();
    }, []);

    const latestTradeValue = useObservableValue(() => marketDataTradeObs, [marketDataTradeObs]);
    const tradeDirection = useObservableValue(() => tradeDirections, [tradeDirections]);

    const handleRowClick = useCallback(
      (values: { side: SideEnum; levelIndex: number; quantity: string; price: string; intent: OrderIntent }) => {
        const rowSide = values.side;

        const orderSide =
          values.intent === AggressorFlagEnum.Active
            ? rowSide === SideEnum.Buy
              ? SideEnum.Sell
              : SideEnum.Buy
            : rowSide === SideEnum.Buy
              ? SideEnum.Buy
              : SideEnum.Sell;

        onRowClick({ side: orderSide, price: values.price, quantity: values.quantity, intent: values.intent });
      },
      [onRowClick]
    );

    const lastPrice = useMemo(() => {
      if (lastPriceType === LastPriceType.LastTraded) {
        return latestTradeValue?.data[0]?.Price ?? '';
      } else {
        const offerPrice = toBigWithDefault(getLevelPrice(offers?.[0], isPricingModeBPS), 0);
        const bidPrice = toBigWithDefault(getLevelPrice(bids?.[0], isPricingModeBPS), 0);
        return offerPrice.eq(0) || bidPrice.eq(0) ? '' : offerPrice.plus(bidPrice).div(2).toFixed();
      }
    }, [lastPriceType, latestTradeValue, bids, offers, isPricingModeBPS]);

    const updateLastTradedType = useCallback(
      (lastPriceType: LastPriceType) => {
        mixpanel.track(MixpanelEvent.ChangeRate);
        setLastPriceType(lastPriceType);
      },
      [mixpanel]
    );
    return (
      <Flex w="100%" flex="1" flexDirection="column" overflow="hidden">
        <Row>
          <Header pl="spacing12" uppercase={true} gap="spacing4" alignItems="center">
            <Box whiteSpace="nowrap">Price {isPricingModeBPS ? 'BPS' : security.QuoteCurrency}</Box>
            {showPriceIncrementSetting ? (
              <Box background="backgroundDefaultButton" borderRadius="borderRadiusDefault">
                <PriceIncrement
                  min={security.MinPriceIncrement ?? '0.01'}
                  value={priceIncrement ?? security.DefaultPriceIncrement ?? '0.01'}
                  onChange={onIncrementChange ?? noop}
                />
              </Box>
            ) : null}
          </Header>
          <Header> </Header>
          <Header justifyContent="center" uppercase={true}>
            {/* No need to show Spread BPS when already in BPS pricing mode */}
            {differenceUnit === SpreadUnit.BPS && !isPricingModeBPS ? 'Spread BPS' : 'Spread'}
          </Header>
          <Header pr="spacing12" justifyContent="flex-end">
            <Tooltip tooltip={`View ${showCumQty ? 'Non-' : ''}Cumulative Quantities`}>
              <Button
                size={FormControlSizes.Tiny}
                startIcon={showCumQty ? IconName.SortCumulative : IconName.SortUnordered}
                onClick={() => handleCumQtyClick(!showCumQty)}
              >
                <Text transform="uppercase">
                  {showCumQty ? 'Cum' : ''} Qty {security.PositionCurrency}
                </Text>
              </Button>
            </Tooltip>
          </Header>
        </Row>
        <Flex flex="0 1 100%" flexDirection="column" minHeight="calc(100% - 26px)" overflow="hidden">
          <Flex
            ref={el => (refs.current[0] = el)}
            flex={`0 0 calc(50% - ${TRADE_DIRECTION_HEIGHT_ADJUSTMENT}px)`}
            flexDirection="column-reverse"
            overflow="auto"
            data-testid="offers-ladder"
          >
            {offers.map((offer, index) => {
              const offerPrice = getLevelPrice(offer, isPricingModeBPS);
              let spread = '';
              if (offerPrice !== '0' && lastPrice) {
                spread =
                  differenceUnit === SpreadUnit.BPS
                    ? spreadBps(offerPrice, lastPrice, SideEnum.Sell, isPricingModeBPS)
                    : getPriceDiff(lastPrice, offerPrice, 4);
              }
              return (
                <OrderBookLevel
                  key={index}
                  security={security}
                  priceIncrement={priceIncrement}
                  side={SideEnum.Sell}
                  total={total}
                  level={offer}
                  levelIndex={index}
                  levelsCount={offers.length}
                  nextLevel={offers?.[index + 1]}
                  ownOrders={myOffers}
                  spread={spread}
                  previewOrderSide={clickToTradePreviewOrder?.side}
                  showCumQty={Boolean(showCumQty)}
                  onClick={handleRowClick}
                  onMouseLeave={onMouseLeaveRow}
                />
              );
            })}
          </Flex>
          <MiddleRowWrapper>
            {clickToTradePreviewOrder ? (
              <ClickToTradeOrderPreview clickToTradePreviewOrder={clickToTradePreviewOrder} security={security} />
            ) : (
              <>
                <TradeDirection security={security} lastPrice={lastPrice} direction={tradeDirection?.Dir ?? 0} />
                <ButtonGroup size={FormControlSizes.Xxs}>
                  {!hideLastTraded ? (
                    <Button
                      disabled={onlyDealersSelected}
                      variant={
                        lastPriceType === LastPriceType.LastTraded ? ButtonVariants.Priority : ButtonVariants.Default
                      }
                      onClick={() => updateLastTradedType(LastPriceType.LastTraded)}
                    >
                      Last Traded
                    </Button>
                  ) : null}
                  <Button
                    variant={
                      lastPriceType === LastPriceType.MarketMid ? ButtonVariants.Priority : ButtonVariants.Default
                    }
                    onClick={() => updateLastTradedType(LastPriceType.MarketMid)}
                  >
                    Market Mid
                  </Button>
                </ButtonGroup>
              </>
            )}
          </MiddleRowWrapper>

          <Flex
            ref={el => (refs.current[1] = el)}
            flex={`0 0 calc(50% - ${TRADE_DIRECTION_HEIGHT_ADJUSTMENT}px)`}
            flexDirection="column"
            overflow="auto"
            data-testid="bids-ladder"
          >
            {bids.map((bid, index) => {
              const bidPrice = getLevelPrice(bid, isPricingModeBPS);
              let spread = '';
              if (bidPrice !== '0' && lastPrice) {
                spread =
                  differenceUnit === SpreadUnit.BPS
                    ? spreadBps(bidPrice, lastPrice, SideEnum.Buy, isPricingModeBPS)
                    : getPriceDiff(bidPrice, lastPrice, 4);
              }
              return (
                <OrderBookLevel
                  key={index}
                  security={security}
                  priceIncrement={priceIncrement}
                  previewOrderSide={clickToTradePreviewOrder?.side}
                  side={SideEnum.Buy}
                  total={total}
                  level={bid}
                  levelIndex={index}
                  levelsCount={bids.length}
                  nextLevel={bids?.[index - 1]}
                  ownOrders={myBids}
                  spread={spread}
                  showCumQty={Boolean(showCumQty)}
                  onClick={handleRowClick}
                  onMouseLeave={onMouseLeaveRow}
                />
              );
            })}
          </Flex>
        </Flex>
      </Flex>
    );
  }
);
